import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  Flame,
  UtensilsCrossed,
  QrCode,
  BarChart3,
  ChefHat,
  Gift,
  Building2,
  Palette,
  Percent,
  FolderTree,
  LogOut,
  Shield,
  UserCheck,
  Eye,
  ChevronDown,
  ChevronRight,
  Pizza,
  Tag,
  Sliders,
  Sparkles,
  Menu as MenuIcon,
  X,
  Users,
  CalendarCheck
} from 'lucide-react';

export const StaffSidebar: React.FC = () => {
  const {
    currentView,
    setCurrentView,
    allOrders,
    currentStaffRole,
    logoutStaff,
    restaurantProfile
  } = useApp();

  const isKitchen = currentStaffRole === 'kitchen';
  const isManager = currentStaffRole === 'manager';
  const isAdmin = currentStaffRole === 'admin';

  const [isMenuSectionOpen, setIsMenuSectionOpen] = useState(false);
  const [isMarketingSectionOpen, setIsMarketingSectionOpen] = useState(false);
  const [isSettingsSectionOpen, setIsSettingsSectionOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const activeCount = allOrders.filter(
    o => o.status !== 'completed' && o.status !== 'cancelled'
  ).length;

  const isMenuGroupActive = currentView.startsWith('staff-menu');
  const isMarketingGroupActive = currentView.startsWith('staff-banners');
  const isSettingsGroupActive = currentView.startsWith('staff-settings');

  const handleNavClick = (viewId: string) => {
    setCurrentView(viewId);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Sticky Top Bar with Toggle */}
      <div className="staff-mobile-bar no-print">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="sidebar-brand-icon" style={{ width: '32px', height: '32px' }}>
            {isKitchen ? <ChefHat size={16} /> : <Flame size={16} />}
          </div>
          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
            {restaurantProfile.name}
          </span>
        </div>

        <button
          className="staff-mobile-toggle-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={20} /> : <MenuIcon size={20} />}
          <span>{isMobileMenuOpen ? 'Close Menu' : 'Staff Menu'}</span>
        </button>
      </div>

      {/* Main Sticky Sidebar */}
      <aside className={`staff-sidebar-container no-print ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Sidebar Header / Brand */}
        <div className="sidebar-brand-box">
          <div className="sidebar-brand-icon">
            {isKitchen ? <ChefHat size={20} /> : <Flame size={20} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="sidebar-brand-name">{restaurantProfile.name}</div>
            <div className="sidebar-role-badge">
              {isKitchen && <ChefHat size={12} />}
              {isManager && <UserCheck size={12} />}
              {isAdmin && <Shield size={12} />}
              <span>
                {isKitchen ? 'Kitchen Staff' : isManager ? 'Restaurant Manager' : 'Admin / Owner'}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation List */}
        <nav className="sidebar-nav-list">
          <div className="sidebar-section-title">MANAGEMENT MENU</div>

          {/* 1. Live Orders (KDS) */}
          <button
            className={`sidebar-nav-btn ${currentView === 'staff-live' ? 'active' : ''}`}
            onClick={() => handleNavClick('staff-live')}
          >
            {isKitchen ? <ChefHat size={18} className="sidebar-btn-icon" /> : <Flame size={18} className="sidebar-btn-icon" />}
            <span className="sidebar-btn-label">{isKitchen ? 'Kitchen Queue (KDS)' : 'Live Orders (KDS)'}</span>
            {activeCount > 0 && <span className="sidebar-badge-count">{activeCount}</span>}
          </button>

          {/* 2. Menu & Dishes with Sub-items Accordion */}
          {(isManager || isAdmin) && (
            <div className="sidebar-group">
              <button
                className={`sidebar-nav-btn ${isMenuGroupActive ? 'group-active' : ''}`}
                onClick={() => {
                  setIsMenuSectionOpen(!isMenuSectionOpen);
                  if (!isMenuGroupActive) {
                    handleNavClick('staff-menu-dishes');
                  }
                }}
              >
                <UtensilsCrossed size={18} className="sidebar-btn-icon" />
                <span className="sidebar-btn-label">Menu & Dishes</span>
                <span style={{ marginLeft: 'auto', opacity: 0.7 }}>
                  {isMenuSectionOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </span>
              </button>

              {/* Submenu Items */}
              {isMenuSectionOpen && (
                <div className="sidebar-submenu-list">
                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-menu-dishes' || currentView === 'staff-menu' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-menu-dishes')}
                  >
                    <span className="submenu-bullet" />
                    <span>Food Items & Dishes</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-menu-categories' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-menu-categories')}
                  >
                    <span className="submenu-bullet" />
                    <span>Menu Categories</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-menu-taxes' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-menu-taxes')}
                  >
                    <span className="submenu-bullet" />
                    <span>Tax & ALV Settings</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 3. Marketing, Banners & Themes with Sub-items Accordion (Admin Only) */}
          {isAdmin && (
            <div className="sidebar-group">
              <button
                className={`sidebar-nav-btn ${isMarketingGroupActive ? 'group-active' : ''}`}
                onClick={() => {
                  setIsMarketingSectionOpen(!isMarketingSectionOpen);
                  if (!isMarketingGroupActive) {
                    handleNavClick('staff-banners-branding');
                  }
                }}
              >
                <Gift size={18} className="sidebar-btn-icon" />
                <span className="sidebar-btn-label">Marketing & Theme</span>
                <span style={{ marginLeft: 'auto', opacity: 0.7 }}>
                  {isMarketingSectionOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </span>
              </button>

              {/* Submenu Items */}
              {isMarketingSectionOpen && (
                <div className="sidebar-submenu-list">
                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-banners-branding' || currentView === 'staff-banners' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-banners-branding')}
                  >
                    <span className="submenu-bullet" />
                    <span>Brand Color & Sound</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-banners-offers' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-banners-offers')}
                  >
                    <span className="submenu-bullet" />
                    <span>Promotional Banners</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-banners-coupons' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-banners-coupons')}
                  >
                    <span className="submenu-bullet" />
                    <span>Smart Coupons</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-banners-hero' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-banners-hero')}
                  >
                    <span className="submenu-bullet" />
                    <span>Hero Cover & Badges</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 4. Table QR Studio */}
          {isAdmin && (
            <button
              className={`sidebar-nav-btn ${currentView === 'staff-qr' ? 'active' : ''}`}
              onClick={() => handleNavClick('staff-qr')}
            >
              <QrCode size={18} className="sidebar-btn-icon" />
              <span className="sidebar-btn-label">Table QR Studio</span>
            </button>
          )}

          {/* 5. Sales & Analytics */}
          {(isManager || isAdmin) && (
            <button
              className={`sidebar-nav-btn ${currentView === 'staff-analytics' ? 'active' : ''}`}
              onClick={() => handleNavClick('staff-analytics')}
            >
              <BarChart3 size={18} className="sidebar-btn-icon" />
              <span className="sidebar-btn-label">Sales & Analytics</span>
            </button>
          )}

          {/* 6. Customers & Feedback CRM */}
          {(isManager || isAdmin) && (
            <button
              className={`sidebar-nav-btn ${currentView === 'staff-customers' ? 'active' : ''}`}
              onClick={() => handleNavClick('staff-customers')}
            >
              <Users size={18} className="sidebar-btn-icon" />
              <span className="sidebar-btn-label">Customers (CRM)</span>
            </button>
          )}

          {/* 7. Event & Private Bookings CRM */}
          {(isManager || isAdmin) && (
            <button
              className={`sidebar-nav-btn ${currentView === 'staff-bookings' ? 'active' : ''}`}
              onClick={() => handleNavClick('staff-bookings')}
            >
              <CalendarCheck size={18} className="sidebar-btn-icon" />
              <span className="sidebar-btn-label">Event Bookings</span>
            </button>
          )}

          {/* 6. Business & Legal Pages with Sub-items Accordion */}
          {isAdmin && (
            <div className="sidebar-group">
              <button
                className={`sidebar-nav-btn ${isSettingsGroupActive ? 'group-active' : ''}`}
                onClick={() => {
                  setIsSettingsSectionOpen(!isSettingsSectionOpen);
                  if (!isSettingsGroupActive) {
                    handleNavClick('staff-settings-business');
                  }
                }}
              >
                <Building2 size={18} className="sidebar-btn-icon" />
                <span className="sidebar-btn-label">Business & Legal</span>
                <span style={{ marginLeft: 'auto', opacity: 0.7 }}>
                  {isSettingsSectionOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </span>
              </button>

              {/* Submenu Items */}
              {isSettingsSectionOpen && (
                <div className="sidebar-submenu-list">
                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-settings-business' || currentView === 'staff-settings' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-settings-business')}
                  >
                    <span className="submenu-bullet" />
                    <span>Restaurant Profile</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-settings-privacy' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-settings-privacy')}
                  >
                    <span className="submenu-bullet" />
                    <span>GDPR Privacy Policy</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-settings-terms' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-settings-terms')}
                  >
                    <span className="submenu-bullet" />
                    <span>Terms of Service</span>
                  </button>

                  <button
                    className={`sidebar-submenu-btn ${currentView === 'staff-settings-allergens' ? 'active' : ''}`}
                    onClick={() => handleNavClick('staff-settings-allergens')}
                  >
                    <span className="submenu-bullet" />
                    <span>Allergens & Oiva</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 7. Staff & User Access Management */}
          {isAdmin && (
            <button
              className={`sidebar-nav-btn ${currentView === 'staff-users' ? 'active' : ''}`}
              onClick={() => handleNavClick('staff-users')}
            >
              <Users size={18} className="sidebar-btn-icon" />
              <span className="sidebar-btn-label">Staff & Users</span>
            </button>
          )}
        </nav>

        {/* Sidebar Footer Controls */}
        <div className="sidebar-footer">
          <button
            className="sidebar-customer-switch-btn"
            onClick={() => handleNavClick('customer')}
            title="Preview Customer Dine-in Screen"
          >
            <Eye size={16} />
            <span>Customer View</span>
          </button>

          <button
            className="sidebar-logout-btn"
            onClick={logoutStaff}
            title="Sign out of staff dashboard"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};
